import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  COOKIE_CONSENT_VALUES,
  getStoredCookieConsent,
  setStoredCookieConsent,
} from '../lib/cookieConsent';

const CookieConsentContext = createContext(null);
const META_REQUIRES_COOKIE_CONSENT = process.env.NEXT_PUBLIC_META_REQUIRES_COOKIE_CONSENT === 'true';

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [isBannerOpen, setIsBannerOpen] = useState(false);

  useEffect(() => {
    const storedConsent = getStoredCookieConsent();
    setConsent(storedConsent);
    setIsBannerOpen(!storedConsent);
    setIsReady(true);
  }, []);

  useEffect(() => {
    if (!isReady || consent) return;

    setIsBannerOpen(true);
  }, [consent, isReady]);

  useEffect(() => {
    if (!isReady || typeof window === 'undefined') return;

    if (consent === COOKIE_CONSENT_VALUES.accepted) {
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          analytics_storage: 'granted',
          ad_storage: 'granted',
          ad_user_data: 'granted',
          ad_personalization: 'granted',
        });
      }
      window.ttq?.enableCookie?.();
      if (META_REQUIRES_COOKIE_CONSENT && typeof window.fbq === 'function') {
        window.fbq('consent', 'grant');
      }
      return;
    }

    if (consent === COOKIE_CONSENT_VALUES.rejected) {
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
        });
      }
      window.ttq?.disableCookie?.();
      if (META_REQUIRES_COOKIE_CONSENT && typeof window.fbq === 'function') {
        window.fbq('consent', 'revoke');
      }
    }
  }, [consent, isReady]);

  const updateConsent = (nextConsent) => {
    setStoredCookieConsent(nextConsent);
    setConsent(nextConsent);
    setIsBannerOpen(false);
  };

  const value = useMemo(
    () => ({
      consent,
      isReady,
      isBannerOpen,
      hasTrackingConsent: consent === COOKIE_CONSENT_VALUES.accepted,
      acceptTracking: () => updateConsent(COOKIE_CONSENT_VALUES.accepted),
      declineTracking: () => updateConsent(COOKIE_CONSENT_VALUES.rejected),
      openBanner: () => setIsBannerOpen(true),
      closeBanner: () => {
        if (consent) {
          setIsBannerOpen(false);
        }
      },
    }),
    [consent, isBannerOpen, isReady]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const value = useContext(CookieConsentContext);

  if (!value) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }

  return value;
}
